import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import MessageBase from './MessageBase';
import FormattedText from '../FormattedText';
import LoadingIndicator from './LoadingIndicator';
import DownloadButton from './DownloadButton';
import MediaModal from './MediaModal';
import useDownloadHandler from '../../hooks/useDownloadHandler';

// Cache global para imágenes ya cargadas fuera del componente
// Esto persistirá incluso si el componente se re-renderiza completamente
const globalImageCache = new Map();

const ImageMessage = ({ text, sender, timestamp, status, media_url, caption, id }) => {
    // Usamos el ID del mensaje para identificación única en el cache
    const messageId = id || `img-${media_url?.substring(media_url?.lastIndexOf('/') + 1) || Date.now()}`;
    
    // Usamos useMemo para que esta comprobación solo se haga una vez por renderizado
    const isImageCached = useMemo(() => globalImageCache.has(media_url), [media_url]);
    
    // Inicializar loading como false si la imagen ya está en caché
    const [loading, setLoading] = useState(!isImageCached);
    const [open, setOpen] = useState(false);
    
    // Importante: Usar useRef para mantener la URL original entre re-renderizados
    const originalUrlRef = useRef(media_url);
    
    // Usar el hook personalizado para la descarga
    const { isDownloading, handleDownload } = useDownloadHandler(
        media_url, 
        '.jpg', 
        'imagen', 
        caption
    );
    
    // Efecto para precargar la imagen - se ejecuta solo en el primer renderizado
    // o cuando cambia la URL (lo que no debería pasar para el mismo mensaje)
    useEffect(() => {
        // Si la imagen ya está en caché, no hacemos nada
        if (globalImageCache.has(media_url)) {
            setLoading(false);
            return;
        }
        
        // Verificar si la URL cambió, lo que podría indicar un problema
        if (originalUrlRef.current !== media_url) {
            console.warn('La URL de la imagen cambió para el mismo mensaje:', messageId);
            originalUrlRef.current = media_url;
        }
        
        // Para casos donde media_url es undefined o null
        if (!media_url) {
            setLoading(false);
            return;
        }
        
        // Crear un nuevo objeto Image para precargar
        const img = new Image();
        
        const onLoad = () => {
            // Marcar en el cache global
            globalImageCache.set(media_url, true);
            setLoading(false);
        };
        
        const onError = () => {
            console.error(`Error al cargar imagen: ${media_url}`);
            setLoading(false);
        };
        
        // Adjuntar controladores
        img.onload = onLoad;
        img.onerror = onError;
        
        // Establecer src para iniciar la carga
        img.src = media_url;
        
        // Limpieza
        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [media_url, messageId]);
    
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    
    // Los manejadores de evento ahora actualizan el cache global
    const handleImageLoad = () => {
        globalImageCache.set(media_url, true);
        setLoading(false);
    };
    
    const handleImageError = () => {
        setLoading(false);
    };
    
    // Método para detectar si el mensaje se está reactivando después de una actualización de Ably
    useEffect(() => {
        // Este efecto se ejecuta cuando Ably actualiza el estado del mensaje
        // Y necesitamos asegurarnos de que no se muestre el loading nuevamente
        if (status && !loading && globalImageCache.has(media_url)) {
            // Forzar loading a false si la imagen ya está en caché
            setLoading(false);
        }
    }, [status, media_url, loading]);
    
    return (
        <MessageBase sender={sender} timestamp={timestamp} status={status}>
            <Box sx={{ paddingBottom: '15px' }}>
                {/* Contenedor para la imagen y el indicador de carga */}
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: loading ? '150px' : 'auto',
                    minHeight: '80px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(0,0,0,0.05)'
                }}>
                    {/* Indicador de carga - solo mostrarlo si realmente está cargando */}
                    {loading && (
                        <LoadingIndicator 
                            contentType="imagen"
                            isLoading={loading}
                            color={sender === 'user' ? 'inherit' : 'primary'}
                            textColor={sender === 'user' ? 'white' : 'text.secondary'}
                        />
                    )}
                    
                    {/* Imagen clickeable */}
                    {media_url && (
                        <Box
                            component="img"
                            src={media_url}
                            alt={caption || ""}
                            onClick={handleOpen}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            sx={{
                                width: '100%',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                opacity: loading ? 0 : 1,
                                transition: 'opacity 0.3s'
                            }}
                        />
                    )}
                    
                    {/* Botones flotantes sobre la imagen - solo visibles cuando no está cargando */}
                    {!loading && media_url && (
                        <Box sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            display: 'flex',
                            gap: 1
                        }}>
                            {/* Botón de descarga */}
                            <DownloadButton 
                                onDownload={handleDownload}
                                isDownloading={isDownloading}
                                fileType="imagen"
                                size="small"
                            />
                            
                            {/* Botón para ampliar */}
                            <Tooltip title="Ampliar imagen">
                                <IconButton
                                    size="small"
                                    onClick={handleOpen}
                                    sx={{
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        color: 'white',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                        }
                                    }}
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                
                {/* Si hay caption, mostrarlo */}
                {caption && (
                    <Box sx={{ mt: 1 }}>
                        <FormattedText text={caption} />
                    </Box>
                )}
                
                {/* Modal para ver la imagen ampliada */}
                <MediaModal 
                    open={open}
                    onClose={handleClose}
                    caption={caption}
                    loading={loading}
                    isDownloading={isDownloading}
                    onDownload={handleDownload}
                    contentType="imagen"
                >
                    {media_url && (
                        <Box
                            component="img"
                            src={media_url}
                            alt={caption || "Imagen ampliada"}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                opacity: loading ? 0 : 1,
                                transition: 'opacity 0.3s'
                            }}
                        />
                    )}
                </MediaModal>
            </Box>
        </MessageBase>
    );
};

export default React.memo(ImageMessage);